"use client";

import React, { useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import CustomInput from "./CustomInput";
import { tripFormSchema } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { createNewTrip } from "@/lib/actions/trip.actions";
import MyCustomFormField, { FormFieldType } from "./MyCustomFormField";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { DropdownMenuDemo } from "./DestinationDropDown";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Card,
  CardContent
} from "./ui/card";
import { Car, Bus, CarTaxiFront } from "lucide-react";


const TripForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedTab, setSelectedTab] = useState("immediate");
  const [selectecSchedule, setSelectecSchedule] = useState<Date | null>(null);
  const [selectedDestinationOption, setSelectedDestinationOption] = useState("0");
  const [selectedDestinationDetail, setSelectedDestinationDetail] = useState("0");
  const [selectedVehiculeOption, setSelectedVehiculeOption] = useState("0");

  const [dropDownDestination, setDropDownDestination] = useState("Paris");

  const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

  const setDataFromDropDown = (
    destinationOption: string,
    destinationDetail: string,
    destination: string
  ) => {
    setError("");
    setSelectedDestinationOption(destinationOption);
    setSelectedDestinationDetail(destinationDetail);
    setDropDownDestination(destination);
  };

  const onTabChange = (value: string) => {
    setSelectedTab(value);
    setError('');
};

  const router = useRouter();

  const formSchema = tripFormSchema();

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      schedule: undefined,
      //
      name: "",
      message_for_taxi: "",
      //
      street: "",
      zipcode: "",
      city_name: "",
    },
  });

  // 2. Define a submit handler.
  const onSubmit = async (form_data: z.infer<typeof formSchema>) => {
    setError("");
    if (selectedTab === 'advance') {
      if (!form_data.schedule){
        setError("Il faut indiquer la date et l'heure pour les réservations à l'avance");
        return;
      }
      if (form_data.schedule < new Date(Date.now() + (30 * 60 * 1000))){
        setError("La réservation doit être dans au moins 30 minutes");
        return;
      }
    }
    //
    if (
      form_data.schedule &&
      [8, 9].includes(form_data.schedule.getHours()) &&
      selectedDestinationOption === "0" &&
      selectedDestinationDetail === "0"
    ) {
      setError(
        "Il faut indiquer la destination (ou l'arrondissement dans Paris) pour les réservations à 8h et 9h"
      );
      return;
    }
    //
    setIsLoading(true);
    console.log("onSubmit", form_data);
    console.log("onSubmit/destination", {
      selectedDestinationOption,
      selectedDestinationDetail,
    });
    try {
      const sent_data = {
        destination_option: selectedDestinationOption,
        destination_detail: selectedDestinationDetail,
        schedule: selectedTab === 'immediate' ? "" : (form_data.schedule?.toISOString() || ""),
        name: form_data.name,
        message_for_taxi: form_data.message_for_taxi!,
        vehicule_option: selectedVehiculeOption,
        street: form_data.street!,
        zipcode: form_data.zipcode!,
        city_name: form_data.city_name!,
      };

      const { data, error } = (await createNewTrip(sent_data)) || {};
      if (error) {
        toast.error(error);
        await delay(2000);
      } else if (data) {
        toast.success("Votre commande a été enregistrée avec succès");
        if (!form_data.schedule) {
          //for immediate wait 5 secondes
          await delay(5000);
        }else{
          //for advance wait 2 secondes
          await delay(2000);
        }
      } else {
        toast.error("Une erreur s'est produite lors de la commande");
        await delay(2000);
      }
    } catch (err) {
      toast.error("Une erreur s'est produite lors de la commande");
      await delay(2000);
      console.log(err);
    }

    setIsLoading(false);
    // clean
    form.reset();
    setSelectecSchedule(null);
    setSelectedVehiculeOption('0');
    setDataFromDropDown("0", "0", "Paris");
    console.log('router.refresh() start');
    router.refresh();
    console.log('router.refresh() end');
  };

  return (
    <section className="flex flex-col justify-center bg-red w-full">
      
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-2"
            autoComplete="off"
          >
            
              <div className="flex  gap-4 w-full items-center justify-between">
                <h2 className="header-2">Nouvelle commande</h2>
                <Popover>
                  <PopoverTrigger asChild>
                    {/* <Button variant="outline">Changer le départ</Button> */}
                  </PopoverTrigger>
                  <PopoverContent className="w-80  bg-white">
                    <div className="grid gap-4 bg-white">
                      <div className="space-y-2">
                        <h4 className="font-medium leading-none">
                          Adresse de départ
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Si elle est différente de l&apos;adresse de votre
                          compte
                        </p>
                      </div>
                      <div className="grid gap-3">
                        <div className="grid grid-cols-2 items-center gap-4">
                          <Label htmlFor="width">Adresse</Label>
                          <CustomInput
                            control={form.control}
                            name={"street"}
                            label={""}
                            placeholder={"1 rue de rivoli"}
                          />
                        </div>
                        <div className="grid grid-cols-2 items-center gap-4">
                          <Label htmlFor="maxWidth">Code postal</Label>
                          <CustomInput
                            control={form.control}
                            name={"zipcode"}
                            label={""}
                            placeholder={"75018"}
                          />
                        </div>
                        <div className="grid grid-cols-2 items-center gap-4">
                          <Label htmlFor="height">Ville</Label>
                          <CustomInput
                            control={form.control}
                            name={"city_name"}
                            label={""}
                            placeholder={"Paris"}
                          />
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex flex-col">
                <p className="text-sm font-medium mb-2">Destination</p>
                <DropdownMenuDemo
                  dropDownDestination={dropDownDestination}
                  setDataFromDropDown={setDataFromDropDown}
                />
              </div>

              <Tabs defaultValue={selectedTab} className="w-full" onValueChange={onTabChange}>
                <TabsList className="grid w-full grid-cols-2 bg-slate-200" >
                  <TabsTrigger
                    value="immediate"
                    className="data-[state=active]:bg-white">
                    Immédiat
                  </TabsTrigger>
                  <TabsTrigger
                    value="advance"
                    className="data-[state=active]:bg-white"
                  >
                    A l&rsquo;avance
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="immediate"></TabsContent>
                <TabsContent value="advance">
                  <Card>
                    <CardContent className="p-0">
                      <MyCustomFormField
                        showTimeSelect={true}
                        fieldType={FormFieldType.DATETIME_PICKER}
                        control={form.control}
                        name="schedule"
                        label=""
                        placeholder="Sélectionner la date"
                        onChangeDate={(date) => {
                          setSelectecSchedule(date);
                        }}
                      />
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              <div className="flex items-center">
                <p className="text-sm font-medium mr-4">Client ou contact</p>
                <MyCustomFormField
                  fieldType={FormFieldType.INPUT}
                  control={form.control}
                  name="name"
                  label=""
                  placeholder="n° de chambre, initiales, etc."
                ></MyCustomFormField>
              </div>

              <div className="flex items-center justify-around">
                  <div className={`flex flex-col items-center gap-1  p-2 rounded-lg ${selectedVehiculeOption === '0' ? 'bg-blue-100' : 'bg-slate-50'}`}
                  onClick={()=>{setSelectedVehiculeOption('0')}}>
                    <CarTaxiFront className="h-10 w-10 text-current  " />
                    <p className={`text-xs ${selectedVehiculeOption === '0' ? 'font-bold' : 'font-normal'}`}>Normal</p>
                  </div>

                  <div className={`flex flex-col items-center gap-1  p-2 rounded-lg ${selectedVehiculeOption === '1' ? 'bg-blue-100' : 'bg-slate-50'}`}
                  onClick={()=>{setSelectedVehiculeOption('1')}}>
                    <Car className="h-10 w-10 text-current  " />
                    <p className={`text-xs ${selectedVehiculeOption === '1' ? 'font-bold' : 'font-normal'}`}>Break</p>
                  </div>

                    <div className={`flex flex-col items-center gap-1  p-2 rounded-lg ${selectedVehiculeOption === '2' ? 'bg-blue-100' : 'bg-slate-50'}`}
                  onClick={()=>{setSelectedVehiculeOption('2')}}>
                    <Bus className="h-10 w-10 text-current  " />
                    <p className={`text-xs ${selectedVehiculeOption === '2' ? 'font-bold' : 'font-normal'}`}>Van</p>
                  </div>
              </div>
              
              <MyCustomFormField
                fieldType={FormFieldType.INPUT}
                control={form.control}
                name="message_for_taxi"
                label="Message pour le taxi"
                placeholder="eg. bagages, etc."
                maxLength={20}
              ></MyCustomFormField>


            {error && (
              <div className="text-red-500 text-sm font-medium">{error}</div>
            )}

            <div className="flex flex-col gap-4">
              <Button type="submit" className="form-btn" disabled={isLoading}>
                {isLoading ? (
                  <div className="flex">
                    <Loader2 size={20} className="animate-spin" /> &nbsp;
                    Attendre...
                  </div>
                ) : selectedTab === 'immediate' ? (
                  "Commander"
                ) : (
                  "Commander à l'avance"
                )}
              </Button>
            </div>
          </form>
        </Form>
        <footer className="flex justify-center gap-1"> </footer>

    </section>
  );
};

export default TripForm;
